import { IconService } from "./component-service";
import ConnectionProfiles from "../../../resources/generated/connection-profiles.json";

/**
 * Type representing all valid connection profile IDs.
 */
type cpID = keyof typeof ConnectionProfiles;

interface ConnectionProfileIconService extends IconService<cpID, ConnectionProfile> {}

/**
 * Enum representing the keys used in a connection profile object.
 */
export enum CPIconKeys {
    CPName = "CP Name",
    HumanReadableName = "Human Readable Name",
    ImageID = "Image ID",
    SpriteID = "Sprite ID"
}

interface ConnectionProfile {
    [CPIconKeys.CPName]: keyof typeof ConnectionProfiles;
    [CPIconKeys.HumanReadableName]: string;
    [CPIconKeys.ImageID]: string;
    [CPIconKeys.SpriteID]: string;
}

/**
 * Service for accessing connection profile metadata like human-readable names and sprite IDs.
 */
class ConnectionProfileService implements ConnectionProfileIconService {
    /**
     * Returns a list of all available connection profile IDs.
     */
    getIDs() {
        return Object.keys(ConnectionProfiles) as Array<cpID>;
    }

    /**
     * Retrieves the human-readable name for a given connection profile ID.
     * @param id - Connection profile ID
     */
    getHumanReadableName(id: cpID) {
        this.checkIDPresent(id);
        return ConnectionProfiles[id]?.[CPIconKeys.HumanReadableName];
    }

    /**
     * Retrieves the sprite ID for a given connection profile.
     * @param id - Connection profile ID
     */
    getSpriteID(id: cpID) {
        this.checkIDPresent(id);
        return ConnectionProfiles[id]?.[CPIconKeys.SpriteID];
    }

    /**
     * Returns full metadata for a connection profile including name, sprite ID, and ID itself.
     * Use `CPIconKeys` enum to access properties safely.
     * @param id - Connection profile ID
     */
    getMetadata(id: cpID): ConnectionProfile {
        this.checkIDPresent(id);
        return ConnectionProfiles[id] as ConnectionProfile;
    }

    private checkIDPresent(id: string) {
        if (id === undefined || id === null) {
            throw new Error(`Invalid Connection Profile ID: ${id}`);
        }
    }
}

const connectionProfileIconService = new ConnectionProfileService();
export default connectionProfileIconService;
